import OTextFont from "../display/texts/OTextFont";
import {OmyConst} from "../OmyConst";
import OTextBitmap from "../display/texts/OTextBitmap";
import OTextNumberBitmap from "../display/texts/OTextNumberBitmap";
import OTextNumberFont from "../display/texts/OTextNumberFont";
import Omy from "../Omy";
import Key from "../../lib/pixi.js-keyboard/Key";
import OMath from "../utils/OMath";
import OContainer from "../display/OContainer";
import OSprite from "../display/sprites/OSprite";
import OButton from "../display/buttons/OButton";
import OButtonTween from "../display/buttons/OButtonTween";
import OTicker from "./OTicker";
import OPoint from "../utils/OPoint";
import OGraphic from "../display/OGraphic";
import OCheckBoxTween from "../display/buttons/OCheckBoxTween";
import OActorSpine from "../display/actors/OActorSpine";
import OParticle from "../display/particles/OParticle";
import ORevoltParticleSequence from "../display/particles/ORevoltParticleSequence";
import ORevoltParticleEmitter from "../display/particles/ORevoltParticleEmitter";
import OParticleContainer from "../display/particles/OParticleContainer";
import OButtonMod from "../display/buttons/OButtonMod";
import OCheckBox from "../display/buttons/OCheckBox";
import gsap from "gsap";
import {PixiPlugin} from "gsap/PixiPlugin.js";
import {MotionPathPlugin} from "gsap/MotionPathPlugin.js";
import OTextInput from "../display/texts/OTextInput";
import ONeutrinoParticles from "../display/particles/ONeutrinoParticles";
import OMultiStyleTextFont from "../display/texts/OMultiStyleTextFont";
import OMultiStyleTextNumberFont from "../display/texts/OMultiStyleTextNumberFont";
import OSlider from "../display/OSlider";

PixiPlugin.registerPIXI(PIXI);
gsap.registerPlugin(PixiPlugin, MotionPathPlugin);

export default class Factory {
    constructor(gameCache) {
        /** @type {GameCache} */
        this._cache = gameCache;
        /** @type {AssetsManager} */
        this._assets = Omy.assets;
        this._multiResolutionsList = [];
    }

    /**
     * format child in container by y coordinate, use 'pos_dy' and 'format'
     * @param container
     */
    formatObjectsByY(container) {
        let startY = NaN;
        let isPort = Omy.isScreenPortrait;
        /** @type {OMY.OContainer} */
        let child;
        for (let i = 0; i < container.children.length; i++) {
            child = container.children[i];
            if (child.json["format"]) {
                if (isNaN(startY)) startY = child.y;
                child.y = startY + (((isPort && child.json.hasOwnProperty("v_pos_dy")) ? child.json["v_pos_dy"] : 0) || child.json["pos_dy"] || 0);
                if (child.oType === OmyConst.OMY_CONTAINER) {
                    if (child.isAlign) {
                        child.alignContainer();
                        child.y += child.height * child.alignY;
                        startY = child.y + child.height * (1 - child.alignY);
                    } else {
                        this.formatObjectsByY(child);
                        startY = child.y + child.height;
                    }
                } else if (child.oType === OmyConst.OMY_SPRITE) {
                    child.y += child.height * child.anchorY;
                    startY = child.y + child.height * (1 - child.anchorY);
                } else {
                    startY = child.y + child.height;
                }

            }
        }
    }

    /**     * @public     */
    parseJson(json) {
        if (!Omy.isDesktop && json) {
            let isIphone = Omy.OS.iPhone;
            for (let jsonKey in json) {
                if (json[jsonKey] === null)
                    continue;
                // Omy.log(jsonKey);
                if (json.hasOwnProperty(jsonKey + "m")) {
                    json[jsonKey] = json[jsonKey + "m"];
                } else if (json.hasOwnProperty("m_" + jsonKey)) {
                    json[jsonKey] = json["m_" + jsonKey];
                } else if (isIphone && json.hasOwnProperty("ios_" + jsonKey)) {
                    json[jsonKey] = json["ios_" + jsonKey];
                } else if (typeof json[jsonKey] === "object" && !Array.isArray(json[jsonKey])) {
                    // Omy.warn("object");
                    this.parseJson(json[jsonKey]);
                }
            }
            let entitiesList = json["entitiesm"] || json["m_entities"] || json["entities"];
            if (entitiesList) {
                for (let i = 0; i < entitiesList.length; i++) {
                    if (typeof entitiesList[i] === "object" && !Array.isArray(entitiesList[i])) {
                        // Omy.warn("object");
                        this.parseJson(entitiesList[i]);
                    }
                }
            }
        }
    }

    /**
     * @param {*}parent
     * @param {number}x, default=0
     * @param {number}y, default=0
     * @param {string}revoltName, default=''
     * @param {boolean}autoStart, default=true
     * @param {number}scaleMod, default=1
     * @returns {ORevoltParticleEmitter}
     */
    createRevoltParticleEmitter(parent = null, x = 0, y = 0, revoltName = "", autoStart = true, scaleMod = 1) {
        let cont = new ORevoltParticleEmitter(revoltName, autoStart, scaleMod);
        cont.setXY(x, y);
        if (parent) {
            parent.addChild(cont);
        }
        return cont;
    }

    revoltParticleEmitter(parent, json = null, revoltName = null) {
        if (json.hasOwnProperty("active") && !json["active"]) return null;
        json = OMY.OMath.jsonCopy(json);

        if (revoltName) {
            json.revoltName = revoltName;
        }
        let cont = this.createRevoltParticleEmitter(parent, json["x"], json["y"], json["revoltName"],
            ((json.hasOwnProperty("autoStart")) ? json["autoStart"] : true), ((json.hasOwnProperty("scaleMod")) ? json["scaleMod"] : 1));
        cont.json = json;
        cont.name = json["name"] || "c_" + OMath.uniqueID();
        if (json["scale"]) {
            cont.scale.set(json["scale"]);
        } else {
            if (json["scaleX"]) {
                cont.scaleX = json["scaleX"];
            }
            if (json["scaleY"]) {
                cont.scaleY = json["scaleY"];
            }
        }

        if (json["rotate"]) cont.rotation = OMath.toRadians(json["rotate"]);

        cont.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;
        cont.visible = json.hasOwnProperty("visible") ? json["visible"] : true;

        if (json["anchor"])
            cont.anchor.set(json["anchor"]);
        if (json["anchorX"] || json["anchorY"])
            cont.setAnchor(json["anchorX"] || 0, json["anchorY"] || 0);
        if (json["debug"]) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [cont]);
        }
        return cont;
    }

    /**
     * @param {*}parent
     * @param {number}x, default=0
     * @param {number}y, default=0
     * @param {string}revoltName, default=''
     * @param {number}delay, default=0
     * @param {boolean}autoStart, default=true
     * @param {number}scaleMod, default=1
     * @returns {ORevoltParticleSequence}
     */
    createRevoltParticleSequence(parent = null, x = 0, y = 0, revoltName = "", delay = 0, autoStart = true, scaleMod = 1) {
        let cont = new ORevoltParticleSequence(revoltName, delay, autoStart, scaleMod);
        cont.setXY(x, y);
        if (parent) {
            parent.addChild(cont);
        }
        return cont;
    }

    revoltParticleSequence(parent, json, revoltName = null) {
        if (json.hasOwnProperty("active") && !json["active"]) return null;
        json = OMY.OMath.jsonCopy(json);

        if (revoltName) {
            json.revoltName = revoltName;
        }
        let cont = this.createRevoltParticleSequence(parent, json["x"] || 0, json["y"] || 0, json["revoltName"],
            json["delay"] || 0, ((json.hasOwnProperty("autoStart")) ? json["autoStart"] : true), ((json.hasOwnProperty("scaleMod")) ? json["scaleMod"] : 1));
        cont.json = json;
        cont.name = json["name"] || "c_" + OMath.uniqueID();
        if (json["scale"]) {
            cont.scale.set(json["scale"]);
        } else {
            if (json["scaleX"]) {
                cont.scaleX = json["scaleX"];
            }
            if (json["scaleY"]) {
                cont.scaleY = json["scaleY"];
            }
        }

        if (json["rotate"]) cont.rotation = OMath.toRadians(json["rotate"]);

        cont.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;
        cont.visible = json.hasOwnProperty("visible") ? json["visible"] : true;

        if (json["anchor"])
            cont.anchor.set(json["anchor"]);
        if (json["anchorX"] || json["anchorY"])
            cont.setAnchor(json["anchorX"] || 0, json["anchorY"] || 0);
        if (json["debug"]) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [cont]);
        }
        return cont;
    }

    /**     * @public     */
    button(type, parent = null, x = 0, y = 0,
           out = null, atlas = null, over = null, down = null, block = null,
           externalMethod = null, methodParams = null, debug = 0) {
        let btn;
        type = type || OmyConst.OMY_BUTTON;
        switch (type) {
            case OmyConst.OMY_BUTTON: {
                btn = new OButton(out, atlas, over, down, block);
                break;
            }
            case OmyConst.OMY_BUTTON_TWEEN: {
                btn = new OButtonTween(out, atlas, over, down, block);
                break;
            }
        }

        btn.setXY(x, y);

        if (externalMethod)
            btn.externalMethod(externalMethod, methodParams);

        if (parent)
            parent.addChild(btn);

        if (debug) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [btn]);
        }

        return btn;
    }

    /**     * @public     */
    buttonJson(parent = null, json = null, externalMethod = null, methodParams = null, btnClass = null) {
        if (json.hasOwnProperty("active") && !json["active"]) return null;
        json = OMY.OMath.jsonCopy(json);

        let btn;
        let type = btnClass || json["type"];
        let atlas = json["atlas"] || null;
        switch (type) {
            case OmyConst.OMY_BUTTON: {
                btn = new OButton(json["out"], atlas, json["over"], json["down"], json["block"], json);
                break;
            }
            case OmyConst.OMY_BUTTON_TWEEN: {
                btn = new OButtonTween(json["out"], atlas, json["over"], json["down"], json["block"], json);
                break;
            }
            case OmyConst.OMY_CHECK_BOX_TWEEN: {
                btn = new OCheckBoxTween(json["out"], atlas, json);
                break;
            }
            case OmyConst.OMY_CHECK_BOX: {
                btn = new OCheckBox(json["out"], atlas, json["over"], json["down"], json["block"], json);
                break;
            }
            default: {
                btn = new btnClass(json["out"], atlas, json["over"], json["down"], json["block"], json);
                break;
            }
        }
        btn.json = json;
        btn.name = json["name"] || "b_" + OMath.uniqueID();
        btn.setXY(json["x"] || 0, json["y"] || 0);
        btn.rotation = OMath.toRadians(json["rotate"]) || 0;
        btn.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1;
        btn.visible = json.hasOwnProperty("visible") ? json["visible"] : true;
        if (json["scale"])
            btn.setScale(json["scale"], json["scale"]);
        else
            btn.setScale(json["scaleX"] || 1, json["scaleY"] || 1);
        if (json["anchor"])
            btn.anchor.set(json["anchor"]);
        if (json["anchorX"] || json["anchorY"])
            btn.setAnchor(json["anchorX"] || 0, json["anchorY"] || 0);

        if (json["hintAreaRect"])
            this.hintArea(btn, json["hintAreaRect"], json["hintAreaRect"]["type"], json["hintAreaRect"]["debug"]);
        if (json["label"])
            btn.label = this.textJson(null, json["label"]);
        if (json["hintLabel"])
            btn.hintLabel = this.textJson(null, json["hintLabel"]);
        if (json["oneClick"])
            btn.oneClick = json["oneClick"];
        if (json["soundUp"])
            btn.soundUp = json["soundUp"];
        if (json["soundOver"])
            btn.soundOver = json["soundOver"];
        if (externalMethod)
            btn.externalMethod(externalMethod, methodParams);
        if (parent)
            parent.addChild(btn);
        if (json["tint"])
            btn.tint = json["tint"];
        if (parent && json.hasOwnProperty("joinObject"))
            btn.regJoinObjects(parent.getChildByName(json["joinObject"]));
        if (json["debug"]) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [btn]);
        }
        this.createEntities(btn, json);

        return btn;
    }

    /** @public */
    btnJson(parent, json, buttonClass) {
        if (!parent) {
            Omy.error("Button can not be created without parent object");
            return;
        }
        if (!json) {
            Omy.error("Button can not be created without configuration object");
            return;
        }
        if (!buttonClass) {
            Omy.error("Button can not be created without buttonClass");
            return;
        }
        if (!(buttonClass.prototype instanceof OButtonMod)) {
            Omy.error("Button class must be extended from OButtonMod");
            return;
        }

        json = OMY.OMath.jsonCopy(json);
        return new buttonClass(parent, json);
    }

    /**
     * Create container from json
     * @public
     * @param {*} parent
     * @param {Object} json
     * @returns {OContainer}
     */
    containerJson(parent, json = null) {
        if (json.hasOwnProperty("active") && !json["active"]) return null;
        json = OMY.OMath.jsonCopy(json);

        let cont = this.container(parent, json["x"], json["y"], false,
            ((json.hasOwnProperty("interactiveChildren")) ? json["interactiveChildren"] : true));
        cont.json = json;
        cont.name = json["name"] || "c_" + OMath.uniqueID();
        if (json["scale"]) {
            cont.scale.set(json["scale"]);
        } else {
            if (json["scaleX"]) {
                cont.scaleX = json["scaleX"];
            }
            if (json["scaleY"]) {
                cont.scaleY = json["scaleY"];
            }
        }

        if (json["rotate"]) cont.rotation = OMath.toRadians(json["rotate"]);

        cont.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;
        cont.visible = json.hasOwnProperty("visible") ? json["visible"] : true;

        this.createEntities(cont, json);

        if (json.hasOwnProperty("centered") && json["centered"])
            cont.setToCenter();
        if (json.hasOwnProperty("align"))
            cont.setAlign(json["align"]);
        if (json.hasOwnProperty("alignContainer"))
            cont.createAlign(json["alignContainer"]);

        if (json.hasOwnProperty("anchor"))
            cont.setAnchor(json["anchor"], json["anchor"]);
        if (json.hasOwnProperty("anchorX") || json.hasOwnProperty("anchorY"))
            cont.setAnchor(json["anchorX"] || 0, json["anchorY"] || 0);
        if (json["soundUp"])
            cont.soundUp = json["soundUp"];
        if (json["soundOver"])
            cont.soundOver = json["soundOver"];
        if (json["tint"])
            cont.tint = json["tint"];
        if (parent && json.hasOwnProperty("joinObject"))
            cont.regJoinObjects(parent.getChildByName(json["joinObject"]));

        if (json["debug"]) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [cont]);
        }

        if (json.hasOwnProperty("interactive"))
            cont.interactive = json["interactive"];
        if (json.hasOwnProperty("saveOnDestroy")) cont.saveOnDestroy = json["saveOnDestroy"];
        if (json.hasOwnProperty("cacheAsBitmap"))
            this.timer(.3, () => {
                cont.cacheAsBitmap = json["cacheAsBitmap"];
            }, cont);

        return cont;
    }

    divJson(parent, json) {
        throw new Error("pls, not use div container. Only normal");
    }

    /**
     * @param {*}parent
     * @param {number}x, default=0
     * @param {number}y, default=0
     * @param {boolean||number}debug
     * @param {boolean}interactiveChildren, default=true
     * @returns {OContainer}
     */
    container(parent = null, x = 0, y = 0, debug = 0, interactiveChildren = true) {
        let cont = new OContainer();
        // cont.interactChild = interactiveChildren;
        cont.setXY(x, y);
        if (parent)
            parent.addChild(cont);
        if (debug) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [cont]);
        }

        return cont;
    }

    /**
     * @param {*}parent
     * @param {object}style, default={}
     * @param {boolean||number}debug
     * @param {boolean}interactiveChildren, default=true
     * @returns {ODiv}
     */
    div(parent = null, style = {}, debug = 0, interactiveChildren = true) {
        throw new Error("pls, not use div container. Only normal");
    }

    /**
     * @param {*}parent
     * @param {number}x, default=0
     * @param {number}y, default=0
     * @param {boolean||number}debug
     * @param {boolean}interactiveChildren, default=false
     * @returns {OParticleContainer}
     */
    particleContainer(parent = null, x = 0, y = 0, debug = 0, interactiveChildren = false) {
        let cont = new OParticleContainer();
        cont.interactChild = interactiveChildren;
        cont.setXY(x, y);
        if (parent)
            parent.addChild(cont);
        if (debug) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [cont]);
        }
        return cont;
    }

    particleContainerJson(parent, json) {
        json = OMY.OMath.jsonCopy(json);
        let cont = this.particleContainer(parent, json["x"], json["y"], json["debug"],
            ((json.hasOwnProperty("interactiveChildren")) ? json["interactiveChildren"] : true));
        cont.json = json;
        cont.name = json["name"] || "cp_" + OMath.uniqueID();
        if (json["scale"]) {
            cont.scale.set(json["scale"]);
        } else {
            if (json["scaleX"]) {
                cont.scaleX = json["scaleX"];
            }
            if (json["scaleY"]) {
                cont.scaleY = json["scaleY"];
            }
        }

        this.createEntities(cont, json);

        if (json["rotate"]) cont.rotation = OMath.toRadians(json["rotate"]);

        cont.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;
        cont.visible = json.hasOwnProperty("visible") ? json["visible"] : true;

        if (json["anchor"])
            cont.anchor.set(json["anchor"]);
        if (json["anchorX"] || json["anchorY"])
            cont.setAnchor(json["anchorX"] || 0, json["anchorY"] || 0);

        if (json.hasOwnProperty("interactive"))
            cont.interactive = json["interactive"];
        return cont;
    }

    /**     * @public     */
    sprite(parent = null, x = 0, y = 0, texture = null, atlas = null, debug = 0) {
        let sprite = new OSprite(texture, atlas);
        sprite.setXY(x, y);
        if (debug) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [sprite]);
        }

        if (typeof texture === "string") {
            if (texture && Omy.loc.checkLocalisation(texture)) {
                sprite.onLocalisation(texture);
            }
        }

        if (parent)
            parent.addChild(sprite);

        return sprite;
    }

    /**     * @public     */
    spriteJson(parent = null, json = null) {
        if (json.hasOwnProperty("active") && !json["active"]) return null;
        json = OMY.OMath.jsonCopy(json);

        let sprite = new OSprite(json["texture"], json["atlas"] || null);
        sprite.json = json;
        sprite.name = json["name"] || "s_" + (json["texture"] + "_" || "") + OMath.uniqueID();
        if (json["texture"] && Omy.loc.checkLocalisation(json["texture"])) {
            sprite.onLocalisation(json["texture"]);
        }
        sprite.json?.loc && sprite.addLocSprite();

        sprite.setXY(json["x"] || 0, json["y"] || 0);

        if (json["scale"]) {
            sprite.scale.set(json["scale"]);
        } else {
            if (json["scaleX"]) {
                sprite.scaleX = json["scaleX"];
            }
            if (json["scaleY"]) {
                sprite.scaleY = json["scaleY"];
            }
        }

        if (json["rotate"]) sprite.rotation = OMath.toRadians(json["rotate"]);

        sprite.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;
        sprite.visible = json.hasOwnProperty("visible") ? json["visible"] : true;

        if (json["anchor"])
            sprite.anchor.set(json["anchor"]);
        if (json["anchorX"] || json["anchorY"])
            sprite.setAnchor(json["anchorX"] || 0, json["anchorY"] || 0);
        if (json["width"])
            sprite.width = json["width"];
        if (json["height"])
            sprite.height = json["height"];
        if (json["soundUp"])
            sprite.soundUp = json["soundUp"];
        if (json["soundOver"])
            sprite.soundOver = json["soundOver"];
        if (json["tint"])
            sprite.tint = json["tint"];

        if (json["hintAreaRect"])
            this.hintArea(sprite, json["hintAreaRect"], json["hintAreaRect"]["type"], json["hintAreaRect"]["debug"]);

        if (json["debug"]) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [sprite]);
        }

        if (parent)
            parent.addChild(sprite);
        if (parent && json.hasOwnProperty("joinObject"))
            sprite.regJoinObjects(parent.getChildByName(json["joinObject"]));

        this.createEntities(sprite, json);

        if (json.hasOwnProperty("interactive")) {
            sprite.interactive = json["interactive"];
        }

        if (json.hasOwnProperty("saveOnDestroy")) sprite.saveOnDestroy = json["saveOnDestroy"];

        return sprite;
    }

    _isArray(obj) {
        return Object.prototype.toString.call(obj) === "[object Array]";
    }

    /**     * @public     */
    emitterJson(parent, json) {
        if (!json)
            Omy.error("This object have not json data. Name:" + "emitterJson");
        if (!parent)
            Omy.error("Need to add parent. Name:" + "emitterJson");
        let emitter = null;

        if (typeof json === "object" && Array.isArray(json)) {
            emitter = [];
            for (let i = 0; i < json.length; i++) {
                emitter.push(this._createEmitter(parent, json[i]));
            }
        } else {
            emitter = this._createEmitter(parent, json);
        }

        return emitter;
    }

    /**     * @private     */
    _createEmitter(parent, json) {
        json = OMY.OMath.jsonCopy(json);
        let texture;
        let config;
        let type;
        /** @type {PIXI.particles.Emitter} */
        let emitter;

        config = Omy.assets.getJSON(json["config"]);

        type = json["type"] || "none";
        if (!config)
            Omy.error("This object have not config data. Name:" + "particle", json);

        if (type === "anim") {
            texture = {};
            texture.framerate = json["framerate"] || 60;
            texture.loop = json["loop"] || false;
            texture.textures = Omy.assets.getAnimation(json["texture"]);
        } else if (json.hasOwnProperty("texture")) {
            if (typeof json["texture"] === "object" && Array.isArray(json["texture"])) {
                texture = [];
                for (let i = 0; i < json['texture'].length; i++) {
                    texture.push(Omy.assets.getTexture(json["texture"][i], json["atlas"] || null));
                }
            } else {
                texture = Omy.assets.getTexture(json["texture"], json["atlas"] || null);
            }
        }
        config = PIXI.particles.upgradeConfig(config, texture);

        emitter = new PIXI.particles.Emitter(parent, config);
        if (type === "path")
            emitter.particleConstructor = PIXI.particles.PathParticle;
        else if (type === "anim")
            emitter.particleConstructor = PIXI.particles.AnimatedParticle;
        return emitter;
    }

    /**     * @public     */
    particleJson(parent = null, json, ParticleClass = null) {
        if (!json)
            Omy.error("This object have not json data. Name:" + "particleJson");
        json = OMY.OMath.jsonCopy(json);

        let particle;
        if (ParticleClass) {
            particle = new ParticleClass(json["maxParticles"] || 1500, json["properties"] || null, json["batchSize"] || 16384, json["autoResize"] || false);

        } else {
            particle = new OParticle(json["maxParticles"] || 1500, json["properties"] || null, json["batchSize"] || 16384, json["autoResize"] || false);

        }
        particle.addEmitter(this.emitterJson(particle, json["emitter"]));

        particle.setXY(json["x"] || 0, json["y"] || 0);
        particle.visible = json.hasOwnProperty("visible") ? json["visible"] : true;
        particle.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;

        if (json["scale"]) {
            particle.scale.set(json["scale"]);
        } else {
            if (json["scaleX"]) {
                particle.scaleX = json["scaleX"];
            }
            if (json["scaleY"]) {
                particle.scaleY = json["scaleY"];
            }
        }

        this.createEntities(particle, json);

        if (json["debug"]) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [particle]);
        }

        if (json.hasOwnProperty("interactive"))
            particle.interactive = json["interactive"];

        if (parent)
            parent.addChild(particle);

        return particle;
    }

    /** @public
     * @param {*}parent
     * @param {Object}json
     * @returns {null|ONeutrinoParticles}
     */
    neutrinoParticlesJson(parent = null, json) {
        if (!json)
            Omy.error("This object have not json data. Name:" + "neutrinoParticlesJson");
        if (json.hasOwnProperty("active") && !json["active"]) return null;
        json = OMY.OMath.jsonCopy(json);

        let particle = new ONeutrinoParticles(json);
        if (json.hasOwnProperty("add_particles")) {
            for (let i = 0; i < json["add_particles"].length; i++) {
                particle.addEmitter(json["add_particles"][i]);
            }
        }
        particle.setXY(json["x"] || 0, json["y"] || 0);
        particle.visible = json.hasOwnProperty("visible") ? json["visible"] : true;
        particle.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;
        particle.name = json["name"] || "np_" + OMath.uniqueID();

        if (json["scale"]) {
            particle.scale.set(json["scale"]);
        } else {
            if (json["scaleX"]) {
                particle.scaleX = json["scaleX"];
            }
            if (json["scaleY"]) {
                particle.scaleY = json["scaleY"];
            }
        }
        if (json["rotate"]) particle.rotation = OMath.toRadians(json["rotate"]);

        if (json["debug"]) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [particle]);
        }

        if (parent)
            parent.addChild(particle);
        if (parent && json.hasOwnProperty("joinObject"))
            particle.regJoinObjects(parent.getChildByName(json["joinObject"]));

        return particle;
    }

    /**     * @public     */
    sliderJson(parent = null, json) {
        if (!json)
            Omy.error("This object have not json data. Name:" + "sliderJson");
        json = OMY.OMath.jsonCopy(json);

        let slider = new OSlider(json["texture"], json["atlas"] || null, json["debug"]);
        slider.json = json;
        slider.name = json["name"] || "sl_" + OMath.uniqueID();
        slider.setXY(json["x"] || 0, json["y"] || 0);

        if (json["scale"]) {
            slider.scale.set(json["scale"]);
        } else {
            if (json["scaleX"]) {
                slider.scaleX = json["scaleX"];
            }
            if (json["scaleY"]) {
                slider.scaleY = json["scaleY"];
            }
        }

        if (json["rotate"]) slider.rotation = OMath.toRadians(json["rotate"]);

        slider.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;
        slider.visible = json.hasOwnProperty("visible") ? json["visible"] : true;

        if (json["hintAreaRect"])
            this.hintArea(slider, json["hintAreaRect"], json["hintAreaRect"]["type"], json["hintAreaRect"]["debug"]);

        if (json["anchor"])
            slider.anchor.set(json["anchor"]);
        if (json["anchorX"] || json["anchorY"])
            slider.setAnchor(json["anchorX"] || 0, json["anchorY"] || 0);

        if (json.hasOwnProperty("vertical"))
            slider.vertical = json["vertical"];
        if (json.hasOwnProperty("minSlideValue"))
            slider.setSliderData(json["minSlideValue"], json["maxSlideValue"]);
        if (json.hasOwnProperty("minTargetValue"))
            slider.setTargetData(json["minTargetValue"], json["maxTargetValue"]);
        if (json["soundUp"])
            slider.soundUp = json["soundUp"];
        if (json["soundOver"])
            slider.soundOver = json["soundOver"];

        if (parent)
            parent.addChild(slider);

        return slider;
    }

    textJson(parent, json = null, textValue = "") {
        if (!json) {
            Omy.error("This object have not json data.");
            return;
        }

        if (json.hasOwnProperty("active") && !json["active"]) return null;
        json = OMY.OMath.jsonCopy(json);

        if (json.fontFamily || json.fontSize) {
            json.font = json.font || {};
            json.font.name = (json.fontFamily) ? `${json.fontFamily}` :
                ((json.fontName) ? `${json.fontName}` : null);
            json.font.size = (json.fontSize) ? `${json.fontSize}` : 10;
        }

        let textField = null;
        let oType = json["type"] || OmyConst.OMY_TEXT_FONT;
        let textString = ((textValue.length) ? textValue : (json["text"]) ? json["text"] : (json["locConst"]) ? json["locConst"] : "0");

        switch (oType) {
            case OmyConst.OMY_TEXT_FONT: {
                if (json.hasOwnProperty("font")) {
                    json["fontFamily"] = json["font"]["name"];
                    json["fontSize"] = json["font"]["size"];
                    delete json["font"];
                }
                textField = new OTextFont(textString, json);
                break;
            }
            case OmyConst.OMY_TEXT_NUMBER_FONT: {
                if (json.hasOwnProperty("font")) {
                    json["fontFamily"] = json["font"]["name"];
                    json["fontSize"] = json["font"]["size"];
                    delete json["font"];
                }
                textField = new OTextNumberFont(textString, json);
                break;
            }
            case OmyConst.OMY_TEXT_BITMAP: {
                if (json.hasOwnProperty("font")) {
                    json["fontName"] = json["font"]["name"];
                    json["fontSize"] = json["font"]["size"];

                    delete json["font"];
                }
                textField = new OTextBitmap(textString, json);
                break;
            }
            case OmyConst.OMY_TEXT_NUMBER_BITMAP: {
                if (json.hasOwnProperty("font")) {
                    json["fontName"] = json["font"]["name"];
                    json["fontSize"] = json["font"]["size"];

                    delete json["font"];
                }
                textField = new OTextNumberBitmap(textString, json);
                break;
            }
            case OmyConst.OMY_TEXT_FONT_INPUT: {
                textField = new OTextInput(textString, json);
                break;
            }
            case OmyConst.OMY_MULTI_STYLE_TEXT_FONT: {
                if (json.hasOwnProperty("default")) {
                    json["fontFamily"] = json["default"]["fontFamily"];
                    json["fontSize"] = json["default"]["fontSize"];
                }
                textField = new OMultiStyleTextFont(textString, json);
                break;
            }
            case OmyConst.OMY_MULTI_STYLE_TEXT_NUMBER_FONT: {
                if (json.hasOwnProperty("default")) {
                    json["fontFamily"] = json["default"]["fontFamily"];
                    json["fontSize"] = json["default"]["fontSize"];
                }
                textField = new OMultiStyleTextNumberFont(textString, json);
                break;
            }
        }

        textField.name = json["name"] || "t_" + OMath.uniqueID();
        textField.setXY(json["x"] || 0, json["y"] || 0);

        if (json["rotate"]) textField.rotation = OMath.toRadians(json["rotate"]);

        if (json["scale"]) {
            textField.scale.set(json["scale"]);
        } else {
            if (json["scaleX"]) {
                textField.scaleX = json["scaleX"];
            }
            if (json["scaleY"]) {
                textField.scaleY = json["scaleY"];
            }
        }
        textField.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;
        textField.visible = json.hasOwnProperty("visible") ? json["visible"] : true;

        if (json["soundUp"])
            textField.soundUp = json["soundUp"];
        if (json["soundOver"])
            textField.soundOver = json["soundOver"];

        if (json["debug"]) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [textField]);
        }

        this.createEntities(textField, json);

        if (parent)
            parent.addChild(textField);
        if (json["splitInsideObj"])
            textField.splitInsideText = textField.getChildByName(json["splitInsideObj"]);
        if (json["splitObj"] && parent)
            textField.splitText = parent.getChildByName(json["splitObj"]);
        if (json["hintAreaRect"])
            this.hintArea(textField, json["hintAreaRect"], json["hintAreaRect"]["type"], json["hintAreaRect"]["debug"]);
        if (parent && json.hasOwnProperty("joinObject"))
            textField.regJoinObjects(parent.getChildByName(json["joinObject"]));
        if (json.hasOwnProperty("saveOnDestroy")) textField.saveOnDestroy = json["saveOnDestroy"];

        return textField;
    }

    /**
     * Create animation actor from sprite sheet
     * @param {*} parent
     * @param {Object|null|string} json
     * @param {string} json.type
     * @param {string} json.cacheName - if it is undefined take nameAnimation
     * @param {string} json.nameAnimation
     * @param {string} json.textures
     * @param {number} [json.speed=1] - animation speed
     * @param {boolean} [json.play=false] - play animation after creation
     * @param {boolean} [json.playLoop=false] - loop animation when play it after creation
     * @param {boolean} [json.debug=false] - setup debug mode for created object
     * @return {OActor|OActorSpine}
     */
    actorJson(parent, json = null) {
        if (!json) {
            Omy.error("This object have not json data. Name:" + "actor");
            return null;
        }

        if (json.hasOwnProperty("active") && !json["active"]) return null;
        json = OMY.OMath.jsonCopy(json);

        let cacheName = json["cacheName"] || json["nameAnimation"];
        let nameAnimation = json["nameAnimation"];
        /** @type {OActor|OActorSpine} */
        let actor;
        switch (json["type"]) {
            case OmyConst.OMY_ACTOR: {
                if (!nameAnimation || !nameAnimation.length)
                    Omy.error("This object have not texture.", json);

                const animationTextures = Omy.assets.getAnimation(nameAnimation);
                if (!animationTextures) {
                    Omy.warn(cacheName + "->" + nameAnimation + " animation will not be added because of missed textures data");
                    Omy.log(json);
                    return;
                }
                actor = this._cache.getActorAtlas(cacheName, animationTextures, nameAnimation);
                actor.fps = json["fps"] || Omy.fps;
                if (actor.active) {
                    if (json["animations"]) {
                        let animO;
                        for (let i = 0; i < json['animations'].length; i++) {
                            animO = json["animations"][i];
                            actor.addAnimation(animO.name, Omy.assets.getAnimation(animO.nameAnimation));
                        }
                    }
                    if (json["playList"])
                        actor.addPlayList(json["playList"]);
                    if (json["framesAnim"])
                        actor.addFramesList(json["framesAnim"]);
                } else {
                    actor.revive();
                }
                if (json.hasOwnProperty("saveOnDestroy")) actor.saveOnDestroy = json["saveOnDestroy"];

                break;
            }
            case OmyConst.OMY_ACTOR_SPINE: {
                if (!json["textures"])
                    Omy.error("This object have not texture.", json);
                actor = new OActorSpine(Omy.assets.getSpineData(json["textures"], json));
                if (json["custom_a_name"])
                    actor.changeAnimation(json["custom_a_name"]);
                if (json["skin"])
                    actor.setSkin(json["skin"]);
                if (json.hasOwnProperty("destroyOnKill"))
                    actor.destroyOnKill = json["destroyOnKill"];
                if (json.hasOwnProperty("logOn"))
                    actor.logOn = json["logOn"];
                break;
            }
        }

        actor.json = json;
        actor.name = json["name"] || "a_" + OMath.uniqueID();
        actor.speed = json["speed"] || 1;
        if (json["totalLoop"])
            actor.totalLoop = json["totalLoop"];

        actor.setXY((json["x"] || 0) + (json["dx"] || 0),
            (json["y"] || 0) + (json["dy"] || 0));

        if (json.hasOwnProperty("scale")) {
            actor.scale.set(json["scale"]);
        } else {
            if (json.hasOwnProperty("scaleX")) {
                actor.scaleX = json["scaleX"];
            }
            if (json.hasOwnProperty("scaleY")) {
                actor.scaleY = json["scaleY"];
            }
        }
        if (json.hasOwnProperty("anchor"))
            actor.anchor.set(json["anchor"]);
        if (json.hasOwnProperty("anchorX") || json.hasOwnProperty("anchorY"))
            actor.setAnchor(json["anchorX"] || 0, json["anchorY"] || 0);
        if (json.hasOwnProperty("rotate")) actor.rotation = OMath.toRadians(json["rotate"]);
        actor.alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;
        actor.visible = json.hasOwnProperty("visible") ? json["visible"] : true;
        if (json["reverse"])
            actor.reverse();
        if (json.hasOwnProperty("removeOnEnd"))
            actor.removeOnEnd = json["removeOnEnd"];

        if (json["debug"]) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [actor]);
        }
        if (parent)
            parent.addChild(actor);

        json["play"] && actor.gotoAndPlay(0, json["playLoop"]);
        if (parent && json.hasOwnProperty("joinObject"))
            actor.regJoinObjects(parent.getChildByName(json["joinObject"]));

        this.createEntities(actor, json);
        return actor;
    }

    /**
     * @param {*}parent
     * @param {object}json
     * @param {int}startFrom
     * @param {String}tName
     * @param {Function}onCreateField
     */
    createTexts(parent, json, startFrom = 1, tName = "t_", onCreateField = null) {
        let i = startFrom - 1 || 0;
        let t;
        while (json[tName + String(++i)]) {
            t = this.textJson(parent, json[tName + String(i)]);
            t.configName = tName + String(i);
            if (!t.name)
                t.name = tName + String(i);
            if (onCreateField)
                onCreateField(t);
        }
        onCreateField = null;
        parent = null;
        json = null;
    }

    /**
     * @param {*}parent
     * @param {object}json
     * @param {int}startFrom
     * @param {String}iName
     * @param {Function}onCreateImg
     */
    createSprites(parent, json, startFrom = 1, iName = "i_", onCreateImg = null) {
        let i = startFrom - 1 || 0;
        let img;
        while (json[iName + String(++i)]) {
            img = this.spriteJson(parent, json[iName + String(i)]);
            img.configName = iName + String(i);
            if (!img.name)
                img.name = iName + String(i);
            if (onCreateImg)
                onCreateImg(img);
        }
        onCreateImg = null;
        parent = null;
        json = null;
        img = null;
    }

    /**     * @public
     * @param {*}parent
     * @param {object}json
     * */
    createEntities(parent, json) {
        if (!json) return;
        json = Array.isArray(json) ? json : json["entities"];
        let l = json ? json.length : 0;
        let db;
        for (let i = 0; i < l; i++) {
            let entityConf = json[i];
            this.parseJson(entityConf);
            let entityType = entityConf["type"];
            if (entityConf.hasOwnProperty("isMobile"))
                if ((entityConf["isMobile"] && Omy.isDesktop) || (!entityConf["isMobile"] && !Omy.isDesktop)) continue;
            if (OMath.inArray([OmyConst.OMY_SPRITE], entityType)) {
                db = this.spriteJson(parent, entityConf);
                if (entityConf.hasOwnProperty("isMask")) this._createMask(parent, db, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_CONTAINER], entityType)) {
                this.containerJson(parent, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_PARTICLE_CONTAINER], entityType)) {
                this.particleContainerJson(parent, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_PARTICLE], entityType)) {
                this.particleJson(parent, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_NEUTRINO_PARTICLES], entityType)) {
                this.neutrinoParticlesJson(parent, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_TEXT_FONT, OmyConst.OMY_TEXT_FONT_INPUT, OmyConst.OMY_TEXT_NUMBER_FONT, OmyConst.OMY_TEXT_BITMAP, OmyConst.OMY_TEXT_NUMBER_BITMAP, OmyConst.OMY_MULTI_STYLE_TEXT_FONT, OmyConst.OMY_MULTI_STYLE_TEXT_NUMBER_FONT], entityType)) {
                this.textJson(parent, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_ACTOR, OmyConst.OMY_ACTOR_SPINE], entityType)) {
                this.actorJson(parent, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_BUTTON, OmyConst.OMY_BUTTON_TWEEN, OmyConst.OMY_CHECK_BOX_TWEEN, OmyConst.OMY_CHECK_BOX], entityType)) {
                this.buttonJson(parent, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_RECT], entityType)) {
                db = this.rectJson(parent, entityConf);
                if (entityConf.hasOwnProperty("isMask")) this._createMask(parent, db, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_REVOLT_EMMITER], entityType)) {
                this.revoltParticleEmitter(parent, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_REVOLT_SEQUENCE], entityType)) {
                this.revoltParticleSequence(parent, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_SLIDER], entityType)) {
                this.sliderJson(parent, entityConf);
            } else if (OMath.inArray([OmyConst.OMY_GRAPHIC], entityType)) {
                db = this.graphicJson(parent, entityConf);
                if (entityConf.hasOwnProperty("isMask")) this._createMask(parent, db, entityConf);
            } else {
                Omy.warn("Unknown type " + entityType);
                Omy.log(entityConf);
            }
        }
    }

    /**     * @private     */
    _createMask(parent, displayObject, json) {
        if (Boolean(json.edit) || Boolean(json.debug)) return;
        if (!json.hasOwnProperty("masked") || !parent.getChildByName(json["masked"])) return;
        parent.getChildByName(json["masked"]).mask = displayObject;
    }

    /** Creates a tween going TO the given values.
     * @link: https://greensock.com/docs/v3/GSAP
     * @link: https://greensock.com/docs/v3/Eases
     * @link: https://greensock.com/docs/v3/GSAP/gsap.to()
     * @param {*}obj
     * @param {Object}params
     * @param {Number}sec
     * @param {Function}onComplete
     * @param {Object}setting
     * @param {gsap.core.Timeline}timeline
     * @param {number | string}position
     * @returns {gsap.core.Tween | gsap.core.Timeline}
     */
    tween(obj, params, sec, onComplete = null, setting = null, timeline = null, position = undefined) {
        // delay:0
        // ease:''  like "none" - default
        // paused:false
        // onStart:Function
        // onUpdate:Function
        // onRepeat:Function
        // repeat:0 (-1 - loop)
        // repeatDelay:0
        // yoyo:false
        // reversed:false
        // onCompleteParams:[]
        // startAt:null TweenMax.to(mc, 2, {x:500, startAt:{x:0}});
        if (onComplete)
            params.onComplete = onComplete;
        /*if (setting && !setting.hasOwnProperty("ease"))
            params.ease = "none";
        if (!params.hasOwnProperty("ease"))
            params.ease = "none";*/
        if (setting) {
            for (let settingKey in setting) {
                params[settingKey] = setting[settingKey];
            }
        }
        if (obj)
            obj.omyTwinned = true;

        if (timeline) {
            params["duration"] = sec;
            return timeline.to(obj, params, position);
        } else {
            return gsap.to(obj, sec, params); // eslint-disable-line
        }
    }

    /**Creates a new timeline, used to compose sequences of tweens.
     * @link: https://greensock.com/docs/v3/GSAP/gsap.timeline()
     * @param {Object}vars
     * @param {Object}defaults
     * @returns {gsap.core.Timeline}
     */
    tweenTimeline(vars = null, defaults = null) {
        // delay:0
        // onComplete:Function
        // onCompleteParams:[]
        // onRepeat:Function
        // onRepeatParams:[]
        // onReverseComplete:Function
        // onReverseCompleteParams:[]
        // onStart:Function
        // onStartParams:[]
        // onUpdate:Function
        // onUpdateParams:[]
        // paused:false
        // repeat:0 (-1 - loop)
        // repeatDelay:0
        // yoyo:false
        // reversed:false
        vars = vars || {};
        if (defaults) {
            vars.defaults = defaults;
        }
        return gsap.timeline(vars); // eslint-disable-line
    }

    /**
     * addd timer ticker
     * @param {Number}time, sec
     * @param {Function}onComplete
     * @param {Object}contextObj
     * @param {Number}repeat
     * @param {Boolean}loop
     * @param {Boolean}removeOnEnd
     * @param {Number}delay, sec
     * @param {Array}params
     * @returns {OTicker}
     */
    timer(time, onComplete, contextObj, repeat = 0, loop = false, removeOnEnd = true, delay = 0, params = []) {
        time = time || 0.0000001;

        let t = new OTicker(time);
        t.loop = loop;
        t.on(onComplete, contextObj, params);
        t.expire = removeOnEnd;
        t.repeat = repeat;
        t.delay = delay;

        Omy.game.addNextTickUpdate(t.start, t);
        return t;
    }

    graphic(parent = null, x = 0, y = 0) {
        let graph = new OGraphic();
        graph.x = x;
        graph.y = y;
        parent && parent.addChild(graph);
        return graph;
    }

    /**
     * Draw rectangle graphics
     * @public
     * @param {*} parent
     * @param {Object} json
     * @param {number} [json.x=0]
     * @param {number} [json.y=0]
     * @param {number} [json.width=1]
     * @param {number} [json.height=1]
     * @param {number} [json.color=0x000000]
     * @param {number} [json.alpha=1.0]
     * @param {string} [json.name]
     * @param {boolean} [json.debug=false]
     * @return {OGraphic}
     */
    rectJson(parent, json) {
        if (!json) {
            Omy.error("Missed configuration object for rectJson");
            return null;
        }

        if (json.hasOwnProperty("active") && !json["active"]) return null;
        json = OMY.OMath.jsonCopy(json);

        let graph = new OGraphic();
        graph.json = json;
        let alpha = json.hasOwnProperty("alpha") ? json["alpha"] : 1.0;
        graph.beginFill(json["color"] || 0x000000, alpha);
        graph.drawRect(0, 0, json["width"] || 1, json["height"] || 1);
        graph.endFill();
        graph.x = json["x"] || 0;
        graph.y = json["y"] || 0;
        graph.name = json["name"] || "r_" + OMath.uniqueID();
        parent && parent.addChild(graph);

        if (json["soundUp"])
            graph.soundUp = json["soundUp"];
        if (json["soundOver"])
            graph.soundOver = json["soundOver"];

        graph.visible = json.hasOwnProperty("visible") ? json["visible"] : true;
        if (json["debug"]) {
            this.timer(0.1, this.regDebugMode, this, 0, false, true, 0, [graph]);
        }
        return graph;
    }

    /**
     * Draw rectangle graphics
     * @public
     * @param {*} parent
     * @param {Object} json
     * @param {number} [json.x=0]
     * @param {number} [json.y=0]
     * @param {number} [json.width=1]
     * @param {number} [json.height=1]
     * @param {number} [json.color=0x000000]
     * @param {number} [json.alpha=1.0]
     * @param {string} [json.name]
     * @param {number} [json.typeDraw=0] 0-rect, 1-circle, 2-ellipse, 3-polygon, 4-roundedrect
     * @param {boolean} [json.debug=false]
     * @return {OGraphic}
     */
    graphicJson(parent, json) {
        if (!json) {
            Omy.error("Missed configuration object for rectJson");
            return null;
        }

        if (json.hasOwnProperty("active") && !json["active"]) return null;
        json = OMY.OMath.jsonCopy(json);

        let graphic = new OGraphic();
        graphic.json = json;
        graphic.drawByJson();
        graphic.x = json["x"] || 0;
        graphic.y = json["y"] || 0;
        graphic.name = json["name"] || "r_" + OMath.uniqueID();
        parent && parent.addChild(graphic);

        if (json["soundUp"])
            graphic.soundUp = json["soundUp"];
        if (json["soundOver"])
            graphic.soundOver = json["soundOver"];

        graphic.visible = json.hasOwnProperty("visible") ? json["visible"] : true;
        if (json["debug"]) {
            this.timer(0.1, this.regDebugMode, this, 0, false, true, 0, [graphic]);
        }
        if (json.hasOwnProperty("saveOnDestroy")) graphic.saveOnDestroy = json["saveOnDestroy"];
        if (parent && json.hasOwnProperty("joinObject"))
            graphic.regJoinObjects(parent.getChildByName(json["joinObject"]));
        return graphic;
    }

    /**     * @public     */
    graphicRectTexture(width = 0, height = 0, tAlpha = 1, color = 0xffffff) {
        let graphicOverlay;
        graphicOverlay = new OGraphic();
        graphicOverlay.position.set(0, 0);
        graphicOverlay.beginFill(color, tAlpha);
        graphicOverlay.drawRect(0, 0, width, height);
        graphicOverlay.endFill();
        return graphicOverlay/*.generateCanvasTexture(PIXI.SCALE_MODES.LINEAR, PIXI.settings.RESOLUTION)*/;
    }

    /**
     * generate pixi texture from displayObject
     * @param {PIXI.DisplayObject}graphic
     * @param {object}option
     * @param {  PIXI.SCALE_MODES}options.scaleMode, LINEAR, NEAREST
     * @param {Number}options.resolution
     * @param {PIXI.MSAA_QUALITY}options.multisample, default=NONE, LOW, MEDIUM, HIGH
     * @param {String}name
     * @param {boolean}deep, default=false
     * @returns {PIXI.RenderTexture}
     */
    generateTextureFrom(graphic, option = null, name = null, deep = false) {
        option = option || {};
        if (option.hasOwnProperty("resolution"))
            option["resolution"] = (typeof option["resolution"] === "boolean") ? PIXI.settings.RESOLUTION : PIXI.settings.RESOLUTION + option["resolution"];
        option.width = graphic.width;
        option.height = graphic.height;
        let texture = PIXI.RenderTexture.create(option);
        if (deep) {
            graphic.updateTransform();
            const point = graphic.getLocalBounds();
            graphic.setTransform();
            let matrix = new PIXI.Matrix(graphic.scaleX, 0, 0, graphic.scaleY, Math.abs(point.x), Math.abs(point.y));
            Omy.renderer.render(graphic, {renderTexture: texture, clear: true, transform: matrix});
        } else {
            Omy.renderer.render(graphic, texture);
        }
        if (name) PIXI.Texture.addToCache(texture, name);
        return texture;
    }

    tint(tAlpha = 1, color = 0x1B0703) {
        return this.graphicRectTexture(Omy.WIDTH, Omy.HEIGHT, tAlpha, color);
    }

    /**     * @public     */
    point(x = 0, y = 0) {
        return new OPoint(x, y);
    }

    /**
     * Add mask graphic by configuration object
     * @param {*} parent
     * @param {Object} json - Configuration object
     * @param {number} json.x=0
     * @param {number} json.y=0
     * @param {number} json.width=100
     * @param {number} json.height=100
     * @param {boolean} json.debug=false
     * @returns {OGraphic}
     */
    maskRectJson(parent, json) {
        return this.maskRect(parent, json["x"], json["y"], json["width"], json["height"], json["debug"]);
    }

    /**
     * Add mask graphic
     * @param {*} parent
     * @param {number} x=0
     * @param {number} y=0
     * @param {number} width=100
     * @param {number} height=100
     * @param {boolean} debug=false
     * @returns {OGraphic}
     */
    maskRect(parent = null, x = 0, y = 0, width = 100, height = 100, debug = false) {
        let mask = this.graphicRectTexture(width, height);
        mask.x = x;
        mask.y = y;
        parent && parent.addChild(mask);

        mask.interactChild = false;
        mask.interactive = false;

        if (debug) {
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [mask]);
            if (Number(debug) !== 2) {
                return null;
            }
        }
        return mask;
    }

    /**
     * Add hit area to graphic object
     * @param {*} parent
     * @param {Object} json
     * @param {number} json.x=0
     * @param {number} json.y=0
     * @param {number} json.width=0
     * @param {number} json.height=0
     * @param {number} json.type=0
     * @param {boolean} json.debug=false
     * @returns {PIXI.Rectangle | PIXI.Circle | PIXI.Ellipse | PIXI.Polygon | PIXI.RoundedRectangle }
     */
    hitAreaJson(parent, json) {
        return this.hintArea(parent, json, json["type"], json["debug"]);
    }

    /**
     * @param parent
     * @param json = {x:0, y:0, width:0, height:0}
     * @param type Rectangle:0 | Circle:1 | Ellipse:2 | Polygon:3 | RoundedRectangle:4
     * @param {boolean}debug =false
     * @returns {PIXI.Rectangle | PIXI.Circle | PIXI.Ellipse | PIXI.Polygon | PIXI.RoundedRectangle }
     */
    hintArea(parent, json = null, type = 0, debug = false) {
        let res = null;
        if (debug) {
            let graphic = new OGraphic();
            graphic.beginFill(0xffffff, 0.3);
            switch (type) {
                case 0: {
                    graphic.drawRect(0, 0, json.width, json.height);
                    break;
                }
                case 1: {
                    graphic.drawCircle(0, 0, json.radius);
                    break;
                }
                case 2: {
                    graphic.drawEllipse(0, 0, json.width, json.height);
                    break;
                }
                case 3: {
                    graphic.drawPolygon(json.points);
                    break;
                }
                case 4: {
                    graphic.drawRoundedRect(0, 0, json.width, json.height, json.radius);
                    break;
                }
            }
            graphic.endFill();
            graphic.setXY(json.x, json.y);
            parent.addChild(graphic);
            this.timer(.1, this.regDebugMode, this, 0, false, true, 0, [graphic]);
        }
        switch (type) {
            case 0: {
                res = new PIXI.Rectangle(json.x, json.y, json.width, json.height);
                break;
            }
            case 1: {
                res = new PIXI.Circle(json.x, json.y, json.radius);
                break;
            }
            case 2: {
                res = new PIXI.Ellipse(json.x, json.y, json.width, json.height);
                break;
            }
            case 3: {
                res = new PIXI.Polygon(json.points);
                break;
            }
            case 4: {
                res = new PIXI.RoundedRectangle(json.x, json.y, json.width, json.height, json.radius);
                break;
            }
        }
        parent.hitArea = res;
        return res;
    }

    /**
     * Add DisplayObject into Layer Group
     * @param {*} displayObject
     * @param {PIXI.display.Group} group
     */
    addToLayer(displayObject, group) {
        displayObject.parentGroup = group;
    }

    regDebugMode(o) {
        if (o._inDebug) return;
        console.error("regDebugMode", o.name, o.texture, o.text, "\n", o);
        Omy.info("Keys:\n" +
            "Key.A, Key.S, Key.W, Key.D - move object;\n" +
            "Key.Q, Key.E - scale (x&y) object;\n" +
            "Key.R - on/off visible;\n" +
            "Key.Z, Key.X - width;\n" +
            "Key.C, Key.V - height;\n" +
            "Key.B, Key.N - angle;\n" +
            "Key.1 - log json;\n" +
            "Key.2 - log text;\n" +
            "Key.3 - save json;");
        o._inDebug = true;
        o.aa_isRegOnDebug = true;
        o.input = true;

        o.logInfo = function() {
            console.log("**********start***********");
            console.log(`"x":${OMath.int(this.x)}, "y":${OMath.int(this.y)}, - position`);
            let dx = this.x - this._saveX + (this.json?.dx || 0);
            let dy = this.y - this._saveY + (this.json?.dy || 0);
            console.log(`"dx":${OMath.int(dx)}, "dy":${OMath.int(dy)}, - position dx`);
            const m = Omy.scaleManager.isScreenPortrait ? "v_" : "m_";
            !Omy.isDesktop && console.log(`"${m}x":${OMath.int(this.x)}, "${m}y":${OMath.int(this.y)}, - position`);
            let x = OMath.roundNumber(this.scaleX, 1000);
            let y = OMath.roundNumber(this.scaleY, 1000);
            console.log("scale \"scaleX\":" + x + ", \"scaleY\":" + y + ", \"width\":" + OMath.int(this.width) + ", \"height\":" + OMath.int(this.height));
            let rotation = OMath.roundNumber(this.rotation, 100);
            console.log("rotation:" + rotation + " \"rotate\":" + OMath.int(OMath.toDegrees(this.rotation)));
            if (o.oType === OmyConst.OMY_ACTOR) {
                console.log("current frame", this.currentFrame);
            }
            if (o.oType && o.oType.indexOf("text") !== -1) {
                console.log("font size", this.fontSize + ((o._fontEx) ? o._fontEx : ""));
                console.log("text", this.text);
            }
            console.info(this.name, this.texture, this.text, "\n", this);
            console.log("**********end***********");
        };
        o.logJson = function() {
            if (this.json) {
                console.warn("type:", this.oType, "name:", this.name);
                Omy.add.debugLogging(JSON.stringify(this.json));
            }
        };
        o.saveJson = function() {
            if (this.json) {
                let m = (Omy.isDesktop) ? "" : Omy.scaleManager.isScreenPortrait ? "v_" : "m_";
                if (this.json["x"] || this.json["y"]) {
                    this.json[m + "x"] = this.x;
                    this.json[m + "y"] = this.y;
                }
                console.group("o ==> saveJson: " + "name:", this.name);
                console.log("this.alpha", this.alpha);
                console.log("scaleX", this.scale.x);
                console.log("scaleY", this.scale.y);
                console.log("width", this.width);
                console.log("height", this.height);
                console.log("rotate", this.rotation);
                if (this.alpha !== 1) this.json["alpha"] = this.alpha;
                if (this.scale.x !== 1 || this.scale.y !== 1) {
                    if (this.scale.x === this.scale.y) {
                        this.json[m + "scale"] = OMath.roundNumber(this.scale.x, 100);
                    } else {
                        if (this.scale.x !== 1) this.json[m + "scaleX"] = OMath.roundNumber(this.scale.x, 100);
                        if (this.scale.y !== 1) this.json[m + "scaleY"] = OMath.roundNumber(this.scale.y, 100);
                    }
                }

                if (this.json[m + "width"]) this.json[m + "width"] = this.width;
                if (this.json[m + "height"]) this.json[m + "height"] = this.height;
                if (this.json[m + "rotate"]) this.json[m + "rotate"] = this.rotation;

                if (this.json["fontSize"]) this.json["fontSize"] = this.fontSize;
                console.groupEnd();
            }
        };
        o.removeFromDebug = function() {
            this.aa_isRegOnDebug = false;
        };
        Omy.keys.registerFunction(Key.DELETE, o.removeFromDebug, o);
        Omy.keys.registerFunction(Key._1, o.logInfo, o);
        Omy.keys.registerFunction(Key._2, o.logJson, o);
        Omy.keys.registerFunction(Key._3, o.saveJson, o);

        const _logPosition = (x, y, dx, dy) => {
            console.log(`"x":${OMath.int(x)}, "y":${OMath.int(y)}, "dx":${OMath.int(dx)}, "dy":${OMath.int(dy)}, - position`);
            const m = Omy.scaleManager.isScreenPortrait ? "v_" : "m_";
            !Omy.isDesktop && console.log(`"${m}x":${OMath.int(x)}, "${m}y":${OMath.int(y)}, "${m}dx":${OMath.int(dx)}, "${m}dy":${OMath.int(dy)}, - position`);
        };

        if (o.oType && o.oType.indexOf("text") !== -1) {
            Omy.info("Keys:\n" +
                "Key.O - font size -;\n" +
                "Key.P - font size +;\n" +
                "Key.L - field scale x +;\n" +
                "Key.K - field scale x -;\n" +
                "Key.J - field scale y +;\n" +
                "Key.H - field scale y -");
            o.checkFontSize = false;
            o.onFontSizeInc = function() {
                this.fontSize++;
                console.log("font size", this.fontSize + ((o._fontEx) ? o._fontEx : ""));
            };
            o.onFontSizeDec = function() {
                this.fontSize--;
                console.log("font size", this.fontSize + ((o._fontEx) ? o._fontEx : ""));
            };
            Omy.keys.registerFunction(Key.P, o.onFontSizeInc, o);
            Omy.keys.registerFunction(Key.O, o.onFontSizeDec, o);

            o.onScaleTextX = function() {
                if (Omy.keys.isDown(Key.K)) {
                    this.scale.x -= .01;
                }
                if (Omy.keys.isDown(Key.L)) {
                    this.scale.x += .01;
                }
                let x = OMath.roundNumber(this.scale.x, 1000);
                console.log("scale \"scaleX\":" + x);
            };
            Omy.keys.registerFunction(Key.L, o.onScaleTextX, o);
            Omy.keys.registerFunction(Key.K, o.onScaleTextX, o);

            o.onScaleTextY = function() {
                if (Omy.keys.isDown(Key.H)) {
                    this.scale.y -= .01;
                }
                if (Omy.keys.isDown(Key.J)) {
                    this.scale.y += .01;
                }
                let y = OMath.roundNumber(this.scale.y, 1000);
                console.log("scale \"scaleY\":" + y);
            };
            Omy.keys.registerFunction(Key.J, o.onScaleTextY, o);
            Omy.keys.registerFunction(Key.H, o.onScaleTextY, o);

            this.timer(.1, o.drawDebugRect, o);

        }

        if (o.oType && o.oType.indexOf("actor") !== -1) {
            Omy.info("Keys:\n" +
                "Key.T - play/stop;\n",
            );
            o.onPlayDebug = function() {
                (this.playing) ? this.stop() : this.play(true);
            };
            Omy.keys.registerFunction(Key.T, o.onPlayDebug, o);


            if (o.oType === OmyConst.OMY_ACTOR_SPINE) {
                Omy.info("Keys:\n" +
                    "Key.Y - gotoAndStop 0;\n" +
                    "Key.L - field scale x +;\n" +
                    "Key.K - field scale x -;\n" +
                    "Key.O - field scale y +;\n" +
                    "Key.P - field scale y -");

                o.onGoFirstDebug = function() {
                    this.reset();
                    this.gotoAndStop(0);
                };
                Omy.keys.registerFunction(Key.Y, o.onGoFirstDebug, o);

                o.onScaleTextX = function() {
                    if (Omy.keys.isDown(Key.K)) {
                        this.scale.x -= .005;
                    }
                    if (Omy.keys.isDown(Key.L)) {
                        this.scale.x += .005;
                    }
                    let x = OMath.roundNumber(this.scale.x, 1000);
                    console.log("scale \"scaleX\":" + x);
                };
                Omy.keys.registerFunction(Key.L, o.onScaleTextX, o);
                Omy.keys.registerFunction(Key.K, o.onScaleTextX, o);

                o.onScaleTextY = function() {
                    if (Omy.keys.isDown(Key.O)) {
                        this.scale.y -= .005;
                    }
                    if (Omy.keys.isDown(Key.P)) {
                        this.scale.y += .005;
                    }
                    let y = OMath.roundNumber(this.scale.y, 1000);
                    console.log("scale \"scaleY\":" + y);
                };
                Omy.keys.registerFunction(Key.O, o.onScaleTextY, o);
                Omy.keys.registerFunction(Key.P, o.onScaleTextY, o);
            }
            if (o.oType === OmyConst.OMY_ACTOR) {
                Omy.info("Keys:\n" +
                    "Key.H - prev frame;\n" +
                    "Key.J - next frame;\n" +
                    "Key.Y - gotoAndStop 0;\n" +
                    "Key.U - gotoAndStop last\n" +
                    "Key.L - field scale x +;\n" +
                    "Key.K - field scale x -;\n" +
                    "Key.O - field scale y +;\n" +
                    "Key.P - field scale y -");
                o.onGoLastDebug = function() {
                    this.gotoAndStop(this.totalFrames - 1);
                };
                Omy.keys.registerFunction(Key.U, o.onGoLastDebug, o);
                o.onPlayNext = function() {
                    this.next();
                    console.log("current frame", this.currentFrame);
                };
                o.onPlayPrevious = function() {
                    this.previous();
                    console.log("current frame", this.currentFrame);
                };
                Omy.keys.registerFunction(Key.H, o.onPlayPrevious, o);
                Omy.keys.registerFunction(Key.J, o.onPlayNext, o);
                o.onGoFirstDebug = function() {
                    this.gotoAndStop(0);
                };
                Omy.keys.registerFunction(Key.Y, o.onGoFirstDebug, o);

                o.onScaleTextX = function() {
                    if (Omy.keys.isDown(Key.K)) {
                        this.scale.x -= .01;
                    }
                    if (Omy.keys.isDown(Key.L)) {
                        this.scale.x += .01;
                    }
                    let x = OMath.roundNumber(this.scale.x, 1000);
                    console.log("scale \"scaleX\":" + x);
                };
                Omy.keys.registerFunction(Key.L, o.onScaleTextX, o);
                Omy.keys.registerFunction(Key.K, o.onScaleTextX, o);

                o.onScaleTextY = function() {
                    if (Omy.keys.isDown(Key.O)) {
                        this.scale.y -= .01;
                    }
                    if (Omy.keys.isDown(Key.P)) {
                        this.scale.y += .01;
                    }
                    let y = OMath.roundNumber(this.scale.y, 1000);
                    console.log("scale \"scaleY\":" + y);
                };
                Omy.keys.registerFunction(Key.O, o.onScaleTextY, o);
                Omy.keys.registerFunction(Key.P, o.onScaleTextY, o);
            }

        }

        if (o.oType && o.oType.indexOf("revolt") !== -1) {
            Omy.info("Keys:\n" +
                "Key.T - play again;\n",
            );
            o.needPlayAgain = function() {
                this.kill();
                try {
                    this.revive();
                } catch (err) {
                }
                this.revive();
                console.log("play from beginning");
            };
            Omy.keys.registerFunction(Key.T, o.needPlayAgain, o);

        }

        if (o.oType && o.oType.indexOf("graphic") !== -1) {
            Omy.info("Keys:\n" +
                "Key.T - redraw graphic;\n" +
                "Key.Y - -1 for radius;\n" +
                "Key.U - +1 for radius;\n",
            );
            o.renderNewGraphic = function() {
                this.drawByJson(true);
                console.log("angle radius", this.json["radius"]);
                console.log("redraw graphic");
            };
            Omy.keys.registerFunction(Key.T, o.renderNewGraphic, o);
            o.radiusDown = function() {
                if (!this.json.hasOwnProperty("radius"))
                    this.json["radius"] = 0;
                this.json["radius"]--;
                console.log("up angle radius", this.json["radius"]);
            };
            Omy.keys.registerFunction(Key.Y, o.radiusDown, o);
            o.radiusUp = function() {
                if (!this.json.hasOwnProperty("radius"))
                    this.json["radius"] = 0;
                this.json["radius"]++;
                console.log("up angle radius", this.json["radius"]);
            };
            Omy.keys.registerFunction(Key.U, o.radiusUp, o);
        }

        if (o.oType && o.oType === OmyConst.OMY_NEUTRINO_PARTICLES) {
            Omy.info("Keys:\n" +
                "Key.T - stop particles;\n" +
                "Key.Y - play particles;\n" +
                "Key.U - restart particles;\n",
            );
            o.onStopNeutrino = function() {
                this.stop();
                console.log("stop particles");
            };
            Omy.keys.registerFunction(Key.T, o.onStopNeutrino, o);

            o.onPlayNeutrino = function() {
                this.play();
                console.log("play particles");
            };
            Omy.keys.registerFunction(Key.Y, o.onPlayNeutrino, o);

            o.onResetNeutrino = function() {
                this.restart();
                console.log("restart particles");
            };
            Omy.keys.registerFunction(Key.U, o.onResetNeutrino, o);
        }

        o.onHide = function() {
            this.visible = !this.visible;
            console.log("visible:", this.visible);
        };
        Omy.keys.registerFunction(Key.R, o.onHide, o);

        o._saveX = o.x;
        o._saveY = o.y;
        o.onMove = function() {
            if (Omy.keys.isDown(Key.A)) {
                this.x -= 1;
            }
            if (Omy.keys.isDown(Key.D)) {
                this.x += 1;
            }
            if (Omy.keys.isDown(Key.W)) {
                this.y -= 1;
            }
            if (Omy.keys.isDown(Key.S)) {
                this.y += 1;

            }
            let x = this.x;
            let y = this.y;
            let dx = x - this._saveX;
            let dy = y - this._saveY;
            _logPosition(x, y, dx, dy);
        };
        Omy.keys.registerFunction(Key.A, o.onMove, o);
        Omy.keys.registerFunction(Key.S, o.onMove, o);
        Omy.keys.registerFunction(Key.W, o.onMove, o);
        Omy.keys.registerFunction(Key.D, o.onMove, o);

        o.voidMethod = function() {
        };
        o.onStartDr = function() {
            let x = this.x;
            let y = this.y;
            this._saveX = x;
            this._saveY = y;
            console.log("start drag \"x\":" + OMath.int(x) + ", \"y\":" + OMath.int(y));
        };
        o.onUptDr = function() {
            let x = this.x;
            let y = this.y;
            let dx = x - this._saveX;
            let dy = y - this._saveY;
            _logPosition(x, y, dx, dy);
        };
        if (o.hasOwnProperty("externalMethod")) {
            o.addUp(o.voidMethod, o);
            o.addDragUpdate(o.onUptDr, o);
        }
        o.drag = true;

        o.onAngle = function() {
            if (Omy.keys.isDown(Key.B)) {
                this.rotation -= 0.01;
            }
            if (Omy.keys.isDown(Key.N)) {
                this.rotation += 0.01;
            }
            let rotation = OMath.roundNumber(this.rotation, 100);
            console.log("rotation:" + rotation + " \"rotate\":" + OMath.int(OMath.toDegrees(this.rotation)));
        };
        Omy.keys.registerFunction(Key.B, o.onAngle, o);
        Omy.keys.registerFunction(Key.N, o.onAngle, o);

        o.onScale = function() {
            if (Omy.keys.isDown(Key.Q)) {
                this.scaleX -= .01;
                this.scaleY -= .01;
            }
            if (Omy.keys.isDown(Key.E)) {
                this.scaleX += .01;
                this.scaleY += .01;
            }
            let x = OMath.roundNumber(this.scaleX, 1000);
            let y = OMath.roundNumber(this.scaleY, 1000);
            console.log("scale \"scaleX\":" + x + ", \"scaleY\":" + y + ", \"width\":" + OMath.int(this.width) + ", \"height\":" + OMath.int(this.height));
        };
        Omy.keys.registerFunction(Key.Q, o.onScale, o);
        Omy.keys.registerFunction(Key.E, o.onScale, o);

        o.onScaleX = function() {
            if (Omy.keys.isDown(Key.Z)) {
                this.width -= 1;
            }
            if (Omy.keys.isDown(Key.X)) {
                this.width += 1;
            }
            let x = OMath.roundNumber(this.scaleX, 1000);
            console.log("scale \"scaleX\":" + x + ", \"width\":" + OMath.int(this.width));
        };
        Omy.keys.registerFunction(Key.Z, o.onScaleX, o);
        Omy.keys.registerFunction(Key.X, o.onScaleX, o);

        o.onScaleY = function() {
            if (Omy.keys.isDown(Key.C)) {
                this.height -= 1;
            }
            if (Omy.keys.isDown(Key.V)) {
                this.height += 1;
            }
            let y = OMath.roundNumber(this.scaleY, 1000);
            console.log("scale \"scaleY\":" + y + ", \"height\":" + OMath.int(this.height));
        };
        Omy.keys.registerFunction(Key.C, o.onScaleY, o);
        Omy.keys.registerFunction(Key.V, o.onScaleY, o);

        o.saveUp = function() {
            let x = this.x;
            let y = this.y;

            let dx = x - this._saveX;
            let dy = y - this._saveY;
            if (Omy.keys.downDuration(Key.A, .5)) {
                this.x -= 1;
                // _logPosition(x, y, dx, dy);
            }
            if (Omy.keys.downDuration(Key.D, .5)) {
                this.x += 1;
                // _logPosition(x, y, dx, dy);
            }
            if (Omy.keys.downDuration(Key.W, .5)) {
                this.y -= 1;
                // _logPosition(x, y, dx, dy);
            }
            if (Omy.keys.downDuration(Key.S, .5)) {
                this.y += 1;
                // _logPosition(x, y, dx, dy);
            }

            if (Omy.keys.downDuration(Key.Z, .5)) {
                this.width -= 1;
                let x = OMath.roundNumber(this.scaleX, 1000);
                // console.log('scale "scaleX":' + x + ', "width":' + OMath.int(this.width));
            }
            if (Omy.keys.downDuration(Key.X, .5)) {
                this.width += 1;
                let x = OMath.roundNumber(this.scaleX, 1000);
                // console.log('scale "scaleX":' + x + ', "width":' + OMath.int(this.width));
            }
            if (Omy.keys.downDuration(Key.C, .5)) {
                this.height -= 1;
                let y = OMath.roundNumber(this.scaleY, 1000);
                // console.log('scale "scaleY":' + y + ', "height":' + OMath.int(this.height));
            }
            if (Omy.keys.downDuration(Key.V, .5)) {
                this.height += 1;
                let y = OMath.roundNumber(this.scaleY, 1000);
                // console.log('scale "scaleY":' + y + ', "height":' + OMath.int(this.height));
            }
            if (Omy.keys.downDuration(Key.Q, .5)) {
                this.scaleX -= .01;
                this.scaleY -= .01;
                let x = OMath.roundNumber(this.scaleX, 1000);
                let y = OMath.roundNumber(this.scaleY, 1000);
                // console.log('scale "scaleX":' + x + ', "scaleY":' + y + ', "width":' + OMath.int(this.width) + ', "height":' + OMath.int(this.height));
            }
            if (Omy.keys.downDuration(Key.E, .5)) {
                this.scaleX += .01;
                this.scaleY += .01;
                let x = OMath.roundNumber(this.scaleX, 1000);
                let y = OMath.roundNumber(this.scaleY, 1000);
                // console.log('scale "scaleX":' + x + ', "scaleY":' + y + ', "width":' + OMath.int(this.width) + ', "height":' + OMath.int(this.height));
            }
            if (Omy.keys.downDuration(Key.B, .5)) {
                this.rotation -= 0.01;
                let rotation = OMath.roundNumber(this.rotation, 100);
                // console.log('rotation:' + rotation + ' "rotate":' + OMath.int(OMath.toDegrees(this.rotation)));
            }
            if (Omy.keys.downDuration(Key.N, .5)) {
                this.rotation += 0.01;
                let rotation = OMath.roundNumber(this.rotation, 100);
                // console.log('rotation:' + rotation + ' "rotate":' + OMath.int(OMath.toDegrees(this.rotation)));
            }

            if (o.oType === OmyConst.OMY_ACTOR) {
                if (Omy.keys.downDuration(Key.H, .5)) {
                    this.previous();
                    // console.log('current frame', this.currentFrame);
                }
                if (Omy.keys.downDuration(Key.J, .5)) {
                    this.next();
                    // console.log('current frame', this.currentFrame);
                }
            }
            if (o.oType === OmyConst.OMY_GRAPHIC) {
                if (Omy.keys.downDuration(Key.Y, .5)) {
                    this.radiusDown();
                }
                if (Omy.keys.downDuration(Key.U, .5)) {
                    this.radiusUp();
                }
            }
        };
        Omy.addUpdater(o.saveUp, o);

        if (o.__proto__ && o.__proto__.hasOwnProperty("destroy")) {
            const destroy = o.destroy.bind(o);
            o.destroy = function() {
                console.warn("Auto remove object from debug when it is destroy");
                OMY.Omy.add.removeFromDebug(this);
                destroy.apply(this, arguments);
            };
        }

        return o;
    }

    /**     * @public     */
    removeFromDebug(o) {
        if (!o._inDebug) return;
        console.error("removeFromDebug", o.name, o.texture, o.text, "\n", o);

        Omy.keys.unregisterFunction(Key.DELETE, o.removeFromDebug, o);
        Omy.keys.unregisterFunction(Key._1, o.logInfo, o);
        Omy.keys.unregisterFunction(Key._2, o.logJson, o);
        Omy.keys.unregisterFunction(Key._3, o.saveJson, o);
        o.input = false;

        o._inDebug = false;
        o.aa_isRegOnDebug = false;
        if (o.oType && o.oType.indexOf("text") !== -1) {
            o.removeDrawDebugRect();
            Omy.keys.unregisterFunction(Key.P, o.onFontSizeInc, o);
            Omy.keys.unregisterFunction(Key.O, o.onFontSizeDec, o);
            Omy.keys.unregisterFunction(Key.L, o.onScaleTextX, o);
            Omy.keys.unregisterFunction(Key.K, o.onScaleTextX, o);
            Omy.keys.unregisterFunction(Key.J, o.onScaleTextY, o);
            Omy.keys.unregisterFunction(Key.H, o.onScaleTextY, o);
        }

        if (o.oType && o.oType.indexOf("actor") !== -1) {
            Omy.keys.unregisterFunction(Key.T, o.onPlayDebug, o);

            if (o.oType === OmyConst.OMY_ACTOR) {
                Omy.keys.unregisterFunction(Key.U, o.onGoLastDebug, o);
                Omy.keys.unregisterFunction(Key.H, o.onPlayPrevious, o);
                Omy.keys.unregisterFunction(Key.J, o.onPlayNext, o);
                Omy.keys.unregisterFunction(Key.Y, o.onGoFirstDebug, o);
                Omy.keys.registerFunction(Key.O, o.onScaleTextY, o);
                Omy.keys.registerFunction(Key.P, o.onScaleTextY, o);
                Omy.keys.registerFunction(Key.L, o.onScaleTextX, o);
                Omy.keys.registerFunction(Key.K, o.onScaleTextX, o);
            }

        }

        if (o.oType && o.oType.indexOf("revolt") !== -1) {
            Omy.keys.unregisterFunction(Key.T, o.needPlayAgain, o);

        }

        if (o.oType && o.oType.indexOf("graphic") !== -1) {
            Omy.keys.unregisterFunction(Key.T, o.renderNewGraphic, o);
            Omy.keys.unregisterFunction(Key.Y, o.radiusDown, o);
            Omy.keys.unregisterFunction(Key.U, o.radiusUp, o);
        }

        if (o.oType && o.oType === OmyConst.OMY_NEUTRINO_PARTICLES) {
            Omy.keys.unregisterFunction(Key.T, o.onStopNeutrino, o);
            Omy.keys.unregisterFunction(Key.Y, o.onPlayNeutrino, o);
            Omy.keys.unregisterFunction(Key.U, o.onResetNeutrino, o);
        }

        Omy.keys.unregisterFunction(Key.R, o.onHide, o);
        Omy.keys.unregisterFunction(Key.A, o.onMove, o);
        Omy.keys.unregisterFunction(Key.S, o.onMove, o);
        Omy.keys.unregisterFunction(Key.W, o.onMove, o);
        Omy.keys.unregisterFunction(Key.D, o.onMove, o);

        o.removeUp(o.voidMethod, o);
        o.removeDragUpdate(o.onUptDr, o);
        o.drag = false;

        Omy.keys.unregisterFunction(Key.B, o.onAngle, o);
        Omy.keys.unregisterFunction(Key.N, o.onAngle, o);
        Omy.keys.unregisterFunction(Key.Q, o.onScale, o);
        Omy.keys.unregisterFunction(Key.E, o.onScale, o);
        Omy.keys.unregisterFunction(Key.Z, o.onScaleX, o);
        Omy.keys.unregisterFunction(Key.X, o.onScaleX, o);
        Omy.keys.unregisterFunction(Key.C, o.onScaleY, o);
        Omy.keys.unregisterFunction(Key.V, o.onScaleY, o);

        Omy.removeUpdater(o.saveUp, o);
        return 0;
    }

    debugLogging(message) {
        if (message.includes(">>>") || message.includes("<<<")) {
            console.groupCollapsed("[stomp]", message.substr(0, message.indexOf("\n")));
            console.log(message);
            console.groupEnd();
        } else {
            console.log(message);
        }
    };
}
